import { useEffect, useMemo, useState } from 'react';
import useGetPharmacyTreatmentMedicineStructures from './useGetPharmacyTreatmentMedicineStructures';
import { PharmacyHandlingMedicineState } from '@/@types/medicine';
import {
  getPharmacyMedicineOptions,
  getPharmacyMedicineStandardOptions,
  parsePharmacyHandlingMedicinesDto
} from '@/parser/medicine';
import useRegisterPharmacyHandlingMedicines from './useRegisterPharmacyHandlingMedicines';
import useUpsertPharmacyHandlingMedicines from './useUpsertPharmacyHandlingMedicines';
import { SelectOption } from '@/assets/data/selectOptions';
import useGetPharmacyHandlingMedicine from './useGetPharmacyHandlingMedicine';
import { validateMinNumber, validateNumberUnit } from '@/utils/validate';

interface ErrorType {
  errorField: boolean[];
  errorMessage: string;
}
interface Props {
  isOpen: boolean;
  editingMedicineId?: string;
  onCloseModal: () => void;
}

const useMedicineModalViewModel = ({
  isOpen,
  editingMedicineId,
  onCloseModal
}: Props) => {
  const [selectedMedicine, setSelectedMedicine] =
    useState<PharmacyHandlingMedicineState>(
      {} as PharmacyHandlingMedicineState
    );
  const [toastMessage, setToastMessage] = useState<string>('');
  const [isMedicineDeleteModalOpen, setIsMedicineDeleteModalOpen] =
    useState(false);
  const [priceErrors, setPriceErrors] = useState<ErrorType>();

  // Custom Hooks
  const { medicineStructures } = useGetPharmacyTreatmentMedicineStructures({
    enabled: isOpen
  });

  const { medicine } = useGetPharmacyHandlingMedicine({
    medicineId: editingMedicineId
  });

  const { registerPharmacyHandlingMedicinesMutate } =
    useRegisterPharmacyHandlingMedicines();

  const { upsertPharmacyHandlingMedicinesMutate } =
    useUpsertPharmacyHandlingMedicines();

  // Select Options
  const pharmacyMedicineOptions = useMemo(
    () =>
      getPharmacyMedicineOptions({
        medicineStructures,
        selectedMedicine
      }),
    [medicineStructures, selectedMedicine.treatmentOption]
  );

  // 약품/규격 및 금액 옵션
  const pharmacyMedicineStandardOptions = useMemo(
    () =>
      getPharmacyMedicineStandardOptions({
        isEdit: !!editingMedicineId,
        medicineStructures,
        selectedMedicine
      }),
    [medicineStructures, selectedMedicine]
  );

  // 규격 및 금액 추가 버튼 활성화 여부
  const isDisabledAddButton = useMemo(() => {
    return (
      pharmacyMedicineStandardOptions?.length ===
      selectedMedicine.medicineStandardPrices?.length
    );
  }, [
    pharmacyMedicineStandardOptions,
    selectedMedicine.medicineStandardPrices
  ]);

  // 토스트 메시지 초기화
  const onClearToastMessage = () => {
    setToastMessage('');
  };

  // 선택된 약품 초기화
  const resetSelectedMedicine = () => {
    setSelectedMedicine({} as PharmacyHandlingMedicineState);
  };

  // 증상 선택시
  const onChangeTreatmentMedicineOption = (treatmentOption: SelectOption) => {
    resetSelectedMedicine();
    setSelectedMedicine(prev => ({
      ...prev,
      treatmentOption
    }));
  };

  // 약품 선택시
  const onChangePharmacyMedicineOption = (medicineOption: SelectOption) => {
    setSelectedMedicine(prev => ({
      ...prev,
      medicineOption
    }));
  };

  // 규격 및 가격 선택시
  const onChangeMedicineStandardOption = (
    option: SelectOption,
    optionFieldIndex: number
  ) => {
    const medicineStandardItem = {
      option,
      price: 0
    };

    const selectedMedicineStandardItem = [
      ...selectedMedicine.medicineStandardPrices
    ];
    selectedMedicineStandardItem[optionFieldIndex] = medicineStandardItem;
    setSelectedMedicine(prev => ({
      ...prev,
      medicineStandardPrices: [...selectedMedicineStandardItem]
    }));
  };

  // 약품 가격 변경시
  const onChangePrice = (value: number, index: number) => {
    if (!/[0-9]$/g.test(String(value))) return;
    const medicineStandardPrices = [...selectedMedicine.medicineStandardPrices];
    medicineStandardPrices[index].price = value;

    setSelectedMedicine(prev => ({
      ...prev,
      medicineStandardPrices
    }));
  };

  // 선택가능한 약품 규격 및 가격 옵션
  const getEnabledPharmacyMedicineStandardOptions = () => {
    const option = (pharmacyMedicineOption: SelectOption) => {
      return !editingMedicineId ? !pharmacyMedicineOption.disabled : true;
    };

    return pharmacyMedicineStandardOptions.filter(
      pharmacyMedicineOption =>
        !selectedMedicine.medicineStandardPrices?.some(
          medicineStandardPrice =>
            pharmacyMedicineOption.value === medicineStandardPrice.option.value
        ) && option(pharmacyMedicineOption)
    );
  };

  // 약품 규격 및 가격 row 추가
  const addUnitInput = () => {
    const enabledStandardPrices = getEnabledPharmacyMedicineStandardOptions();

    if (!!enabledStandardPrices.length) {
      setSelectedMedicine(prev => ({
        ...prev,
        medicineStandardPrices: [
          ...prev.medicineStandardPrices,
          {
            option: enabledStandardPrices[0],
            price: 0
          }
        ]
      }));
    }
  };

  // 약품 규격 및 가격 row 삭제
  const onDeletePrice = (index: number) => {
    setSelectedMedicine(prev => ({
      ...prev,
      medicineStandardPrices: prev.medicineStandardPrices.filter(
        (_, i) => i !== index
      )
    }));
  };

  // 약품 추가 및 수정 버튼 활성화 여부
  const isDisableSubmit = useMemo(() => {
    if (!selectedMedicine.medicineStandardPrices?.length) {
      if (!!editingMedicineId) {
        return false;
      }
      return true;
    }

    if (
      selectedMedicine.medicineStandardPrices.every(
        item =>
          validateMinNumber(item.price, 1000) &&
          validateNumberUnit(item.price, 10)
      )
    ) {
      return false;
    }
    return true;
  }, [selectedMedicine.medicineStandardPrices, editingMedicineId]);

  const onCheckPriceError = () => {
    const isErrorPrices = selectedMedicine.medicineStandardPrices.map(
      item => !validateMinNumber(item.price, 1000)
    );

    if (isErrorPrices.includes(true)) {
      setPriceErrors({
        errorField: isErrorPrices,
        errorMessage: '금액은 1,000원 이상으로 입력해주세요.'
      });
      return;
    }

    const isErrorPriceUnit = selectedMedicine.medicineStandardPrices.map(
      item => !validateNumberUnit(item.price, 10)
    );
    if (isErrorPriceUnit.includes(true)) {
      setPriceErrors({
        errorField: isErrorPriceUnit,
        errorMessage: '1원 단위는 입력이 불가해요.'
      });
      return;
    }
    setPriceErrors(undefined);
  };

  // 약품 추가 및 수정 성공시
  const onSuccessMedicineMutate = async (successMessage: string) => {
    await setToastMessage(successMessage);
    await onCloseModal();
  };

  // 약품 추가
  const onRegisterPharmacyHandlingMedicines = () => {
    registerPharmacyHandlingMedicinesMutate(
      parsePharmacyHandlingMedicinesDto(selectedMedicine),
      {
        onSuccess: () => onSuccessMedicineMutate('약품이 추가되었어요.')
      }
    );
  };

  // 약품 수정
  const onUpsertPharmacyHandlingMedicines = () => {
    upsertPharmacyHandlingMedicinesMutate(
      parsePharmacyHandlingMedicinesDto(selectedMedicine),
      {
        onSuccess: () => onSuccessMedicineMutate('약품 정보가 수정되었어요.')
      }
    );
  };

  // 약품 삭제 모달 on/off
  const onChangeMedicineDeleteModalStatus = () => {
    setIsMedicineDeleteModalOpen(!isMedicineDeleteModalOpen);
  };

  // 약품 추가 및 수정
  const onSubmit = () => {
    if (!editingMedicineId) {
      onRegisterPharmacyHandlingMedicines();
      return;
    }

    if (!selectedMedicine.medicineStandardPrices.length) {
      return onChangeMedicineDeleteModalStatus();
    }
    onUpsertPharmacyHandlingMedicines();
  };

  // 약품 추가일 경우 약품 선택시 규격 및 금액 초기값 설정
  // 약품 수정일 경우 return;
  useEffect(() => {
    if (medicine) return;
    if (selectedMedicine.medicineOption) {
      const enabledOptions = [...pharmacyMedicineStandardOptions].filter(
        options => !options.disabled
      );
      if (!!enabledOptions.length) {
        setSelectedMedicine(prev => ({
          ...prev,
          medicineStandardPrices: [
            {
              option: enabledOptions[0],
              price: 0
            }
          ]
        }));
      }
    }
  }, [selectedMedicine.medicineOption]);

  // 약품 수정일 경우 초기값 설정
  useEffect(() => {
    if (medicine) {
      setSelectedMedicine(medicine);
    }
  }, [medicine]);

  return {
    medicineStructures,
    toastMessage,
    onClearToastMessage,
    selectedMedicine,
    pharmacyMedicineOptions,
    pharmacyMedicineStandardOptions,
    onChangeTreatmentMedicineOption,
    onChangePharmacyMedicineOption,
    onChangeMedicineStandardOption,
    onChangePrice,
    addUnitInput,
    onDeletePrice,
    isDisableSubmit,
    isMedicineDeleteModalOpen,
    onChangeMedicineDeleteModalStatus,
    onUpsertPharmacyHandlingMedicines,
    priceErrors,
    onCheckPriceError,
    isDisabledAddButton,
    onSubmit
  };
};

export default useMedicineModalViewModel;
